import { useEffect, useMemo, useState } from 'react'
import supabase from '../lib/supabase'

const CACHE_TTL_MS = 60_000
const contentCache = new Map()

// Every EditableContent instance on a page shares one request and one result.
// In-flight promise deduplication prevents the N-identical-GETs-on-mount issue.
function fetchPageContent(page) {
  const now = Date.now()
  const cached = contentCache.get(page)
  if (cached?.promise) return cached.promise
  if (cached && now < cached.expiresAt) return Promise.resolve(cached.rows)

  const promise = (async () => {
    const { data, error } = await supabase
      .from('editable_content_view')
      .select('id, page, section, content_key, content, content_html')
      .eq('page', page)

    if (error) throw error
    const rows = data ?? []
    contentCache.set(page, { rows, expiresAt: Date.now() + CACHE_TTL_MS })
    return rows
  })()

  contentCache.set(page, { promise, expiresAt: 0 })
  promise.catch(() => {
    // Allow retry after errors rather than retaining a rejected promise.
    if (contentCache.get(page)?.promise === promise) contentCache.delete(page)
  })
  return promise
}

export function invalidateEditableContent(page) {
  contentCache.delete(page)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('editable-content-updated', { detail: { page } }))
  }
}

/**
 * Loads all editable content for a page with shared in-flight deduplication
 * and a short-lived cache across multiple component instances.
 */
export function useEditableContent(page) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await fetchPageContent(page)
        if (!cancelled) setRows(data)
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Unknown error')
          if (import.meta.env.DEV) console.error(`Error fetching content for page: ${page}`, e)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (page) load()
    else setLoading(false)

    const onUpdated = (event) => {
      if (event.detail?.page === page) load()
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('editable-content-updated', onUpdated)
    }
    return () => {
      cancelled = true
      if (typeof window !== 'undefined') {
        window.removeEventListener('editable-content-updated', onUpdated)
      }
    }
  }, [page])

  const byKey = useMemo(() => {
    const map = {}
    for (const row of rows) {
      if (row.section && row.content_key) {
        map[`${row.section}.${row.content_key}`] = row
      }
    }
    return map
  }, [rows])

  return { rows, byKey, loading, error }
}
